
import React, { useState, useRef, useEffect } from 'react';
import { Send, X, Settings } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChatMessage } from './ChatMessage';
import { sendMessageToOpenAI, getApiKey, setApiKey } from '../utils/aiService';
import { toast } from "@/hooks/use-toast";

interface Message {
  text: string;
  isUser: boolean;
  role?: 'user' | 'assistant' | 'system';
}

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { text: "Hello! I'm your AI assistant. How can I help you today?", isUser: false, role: 'assistant' }
  ]);
  const [input, setInput] = useState('');
  const [apiKey, setApiKeyState] = useState(getApiKey() || '');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Check for API key on load
  useEffect(() => {
    const key = getApiKey();
    if (key) {
      setApiKeyState(key);
    } else {
      // If no API key, prompt user to add one
      setIsConfigOpen(true);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;
    
    // Add user message
    const userMessage: Message = { text: input, isUser: true, role: 'user' };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    
    try {
      // Check if we have API key
      if (!getApiKey()) {
        setIsConfigOpen(true);
        toast({
          title: "API Key Required",
          description: "Please enter your OpenAI API key to continue",
        });
        setIsLoading(false);
        return;
      }
      
      // Format messages for OpenAI API
      const apiMessages = messages
        .concat(userMessage)
        .map(msg => ({
          role: msg.role || (msg.isUser ? 'user' : 'assistant'),
          content: msg.text
        }));
      
      // Add system message if not present
      if (!apiMessages.some(msg => msg.role === 'system')) {
        apiMessages.unshift({
          role: 'system',
          content: 'You are a helpful assistant for a telehealth and online pharmacy website. Provide concise, accurate information about medications and health topics. If asked about specific medications, emphasize the importance of consulting healthcare professionals. Do not provide medical advice or diagnoses.'
        });
      }
      
      // Send to OpenAI
      const aiResponse = await sendMessageToOpenAI(apiMessages);
      
      // Add AI response to messages
      setMessages(prev => [...prev, { 
        text: aiResponse, 
        isUser: false,
        role: 'assistant'
      }]);
    } catch (error) {
      console.error('Error getting AI response:', error);
      // Show a fallback message if API call fails
      setMessages(prev => [...prev, { 
        text: "I'm sorry, I couldn't process your request at the moment. Please try again later.", 
        isUser: false,
        role: 'assistant'
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveApiKey = () => {
    if (!apiKey.trim()) {
      toast({
        title: "Error",
        description: "Please enter a valid API key",
        variant: "destructive"
      });
      return;
    }
    
    if (setApiKey(apiKey)) {
      setIsConfigOpen(false);
      toast({
        title: "Success",
        description: "API key saved successfully",
      });
    }
  };

  return (
    <div className="fixed bottom-5 right-5 z-50">
      {!isOpen ? (
        <Button 
          onClick={() => setIsOpen(true)}
          className="h-12 w-12 rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90"
          aria-label="Open chat"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-message-circle"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
        </Button>
      ) : (
        <div className="w-80 sm:w-96 h-96 bg-background border rounded-lg shadow-lg flex flex-col">
          <div className="p-3 border-b flex justify-between items-center bg-primary text-primary-foreground rounded-t-lg">
            <h3 className="font-medium">AI Assistant</h3>
            <div className="flex gap-2">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setIsConfigOpen(!isConfigOpen)}
                className="h-8 w-8 p-0 text-primary-foreground hover:bg-primary/90"
                aria-label="Settings"
              >
                <Settings className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setIsOpen(false)}
                className="h-8 w-8 p-0 text-primary-foreground hover:bg-primary/90"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          {isConfigOpen ? (
            <div className="flex-1 p-4 flex flex-col">
              <h4 className="font-medium mb-2">OpenAI API Key</h4>
              <p className="text-sm text-gray-600 mb-4">
                Enter your OpenAI API key to use the AI assistant. Your key is stored locally in your browser and never sent to our servers.
              </p>
              <Input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKeyState(e.target.value)}
                placeholder="sk-..."
                className="mb-4"
              />
              <div className="flex gap-2 mt-auto">
                <Button 
                  variant="outline" 
                  onClick={() => setIsConfigOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleSaveApiKey}
                  className="flex-1"
                >
                  Save
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto p-3 space-y-3">
                {messages.map((message, index) => (
                  <ChatMessage key={index} message={message.text} isUser={message.isUser} />
                ))}
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="max-w-[80%] p-3 rounded-lg bg-muted rounded-tl-none">
                      <p className="text-sm">Thinking...</p>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
              
              <form onSubmit={handleSubmit} className="p-3 border-t flex gap-2">
                <Input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Type your message..."
                  className="flex-1"
                  disabled={isLoading}
                />
                <Button type="submit" size="sm" className="px-3" disabled={isLoading}>
                  <Send className="h-4 w-4" />
                </Button>
              </form>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatBot;
